import { z } from 'zod';
import type { PortalRolle, RechtstextDokument } from './portal';
import { VersandStatus } from './nutzer';

/**
 * Händlerportal Teil 2: Team (Inhaber und weitere Zugänge), Verträge und Portal-Ansicht des Marktplatzes
 * (staff_*, meine_vertraege, vertrag_*, sso_einloesen, admin_leave der PHP-Middleware).
 */

/* ------------------------------------------------------------------ Rechte je Rolle */

/** Bereiche des Portals, die je Rolle freigeschaltet sind */
export const PORTAL_RECHTE = {
  produkte: 'Produkte',
  bestellungen: 'Bestellungen',
  auszahlungen: 'Auszahlungen',
  konto: 'Stammdaten und Einstellungen',
  team: 'Team',
  vertraege: 'Verträgen zustimmen',
  werbung: 'Werbung und Guthaben',
} as const;
export type PortalRecht = keyof typeof PORTAL_RECHTE;

/**
 * Wie portal_rolle/require_owner der PHP-Middleware: Inhaber darf alles, Buchhaltung zusätzlich zu Produkten und
 * Bestellungen die Auszahlungen, Mitarbeiter nur Produkte und Bestellungen. Einstellungen, Team und Verträge nur der Inhaber.
 */
export const PORTAL_ROLLEN_RECHTE: Record<PortalRolle, readonly PortalRecht[]> = {
  inhaber: ['produkte', 'bestellungen', 'auszahlungen', 'konto', 'team', 'vertraege', 'werbung'],
  buchhaltung: ['produkte', 'bestellungen', 'auszahlungen', 'werbung'],
  mitarbeiter: ['produkte', 'bestellungen'],
};

export const portalDarf = (rolle: PortalRolle, recht: PortalRecht): boolean => PORTAL_ROLLEN_RECHTE[rolle].includes(recht);

/** Rollen, die der Inhaber vergeben kann, mit Beschreibung für die Auswahl */
export const PORTAL_TEAM_ROLLEN = {
  mitarbeiter: 'Produkte und Bestellungen – ohne Auszahlungen',
  buchhaltung: 'Produkte, Bestellungen und Auszahlungen',
} as const;
export type PortalTeamRolle = keyof typeof PORTAL_TEAM_ROLLEN;

/** Höchstens drei weitere Zugänge neben dem Inhaber (max_mitarbeiter) */
export const PORTAL_MAX_MITARBEITER = 3;

/* ------------------------------------------------------------------ Team */

const Name = z.string().trim().min(2, 'Bitte Namen angeben').max(120, 'Höchstens 120 Zeichen');
const Email = z.string().trim().toLowerCase().email('Bitte gültige E-Mail angeben').max(190);
const TeamRolle = z.enum(['mitarbeiter', 'buchhaltung'], { errorMap: () => ({ message: 'Bitte Rolle wählen' }) });

export const PortalTeamEinladenSchema = z.object({ name: Name, email: Email, rolle: TeamRolle });
export type PortalTeamEinladen = z.infer<typeof PortalTeamEinladenSchema>;

export const PortalTeamAendernSchema = z.object({ name: Name, rolle: TeamRolle, aktiv: z.boolean() });
export type PortalTeamAendern = z.infer<typeof PortalTeamAendernSchema>;

export interface PortalTeamMitglied {
  id: string;
  name: string;
  email: string;
  rolle: PortalRolle;
  aktiv: boolean;
  /** Einladung noch nicht angenommen (kein Passwort festgelegt) */
  eingeladen: boolean;
  zweiFaktorAktiv: boolean;
  erstelltAm: string;
  letzteAnmeldung: string | null;
  /** die angemeldete Person */
  selbst: boolean;
}

export interface PortalTeam {
  mitglieder: PortalTeamMitglied[];
  max: number;
}

export interface PortalTeamErgebnis {
  mitglied: PortalTeamMitglied;
  /** Einmal-Link zum Festlegen des Passworts – zum Weitergeben, falls die E-Mail nicht ankommt */
  link: string;
  versand: z.infer<typeof VersandStatus>;
}

/* ------------------------------------------------------------------ Verträge */

/** Erklärungen bei der Zustimmung zu einer neuen Fassung (vertrag_zustimmen) */
export const VERTRAG_ERKLAERUNGEN = {
  vertrag_akzeptiert: 'Bitte akzeptieren Sie den Händlervertrag',
  unternehmer_bestaetigt: 'Bitte bestätigen Sie, dass Sie als Unternehmer handeln',
} as const;

export const PortalVertragZustimmenSchema = z
  .object({
    sha256: z.string().regex(/^[a-f0-9]{64}$/, 'Bitte den Vertrag neu laden'),
    zustimmungen: z.array(z.string().max(40)).max(10).default([]),
  })
  .superRefine((d, ctx) => {
    for (const [k, text] of Object.entries(VERTRAG_ERKLAERUNGEN)) {
      if (!d.zustimmungen.includes(k)) ctx.addIssue({ code: 'custom', path: [k], message: text });
    }
  });
export type PortalVertragZustimmen = z.input<typeof PortalVertragZustimmenSchema>;

export interface PortalVertragEintrag {
  id: string;
  dokument: Extract<RechtstextDokument, 'haendlervertrag' | 'datenschutz'>;
  titel: string;
  version: string;
  stand: string;
  zeitpunkt: string;
  /** wer zugestimmt hat */
  name: string;
  erklaerungen: string[];
}

export interface PortalVertraege {
  aktuell: { version: string; stand: string };
  /** keine Zustimmung zur aktuellen Version des Händlervertrags */
  zustimmungErforderlich: boolean;
  /** Inhaber (nicht in der Portal-Ansicht des Marktplatzes) */
  darfZustimmen: boolean;
  zustimmungen: PortalVertragEintrag[];
}

/** Vertragstext – aktuell mit den Angaben des Händlers oder genau der akzeptierte Text */
export interface PortalVertragText {
  dokument: RechtstextDokument;
  titel: string;
  html: string;
  version: string;
  stand: string;
  sha256: string;
  /** Zeitpunkt der Zustimmung, null = aktuelle Fassung */
  zeitpunkt: string | null;
}

/* ------------------------------------------------------------------ Portal-Ansicht des Marktplatzes */

export interface PortalAnsichtLink {
  url: string;
  gueltigBis: string;
}

export const PortalSsoSchema = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{43}$/) });
